import { generateErrorId } from "#shared/errors/errorId.ts";
import {
  getErrorDefinition,
  normalizeErrorCode,
  type ErrorCode,
  type ErrorDefinition,
} from "#shared/errors/registry.ts";
import { ApiRequestError } from "@web/lib/api.ts";

export interface ResolvedError {
  definition: ErrorDefinition;
  code: ErrorCode;
  errorId: string;
  /** Prefer registry message; keep server detail when UNKNOWN / research. */
  message: string;
  title: string;
  serverMessage: string | null;
}

/** Turn any thrown value into a registry-backed error for UI. */
export function resolveError(
  err: unknown,
  fallbackCode: ErrorCode = "UNKNOWN",
): ResolvedError {
  if (err instanceof ApiRequestError) {
    let code: ErrorCode =
      err.status === 0
        ? "API_UNREACHABLE"
        : normalizeErrorCode(err.code, err.message);
    if (code === "UNKNOWN" && fallbackCode !== "UNKNOWN") {
      code = fallbackCode;
    }
    const definition = getErrorDefinition(code);
    const keepServerDetail =
      code === "UNKNOWN" ||
      code === "RESEARCH_JSON_INVALID" ||
      code === "SPOTIFY_TOKEN_EXPIRED" ||
      (fallbackCode === "SAVE_FAILED" && err.message.length > 0);
    return {
      definition,
      code,
      errorId: err.errorId ?? generateErrorId(),
      message: keepServerDetail && err.message ? err.message : definition.message,
      title: definition.title,
      serverMessage: err.message,
    };
  }

  if (err instanceof TypeError && /fetch|network|failed/i.test(err.message)) {
    const definition = getErrorDefinition("API_UNREACHABLE");
    return {
      definition,
      code: "API_UNREACHABLE",
      errorId: generateErrorId(),
      message: definition.message,
      title: definition.title,
      serverMessage: err.message,
    };
  }

  const message = err instanceof Error ? err.message : "Unknown error";
  let code = normalizeErrorCode(undefined, message);
  if (code === "UNKNOWN" && fallbackCode !== "UNKNOWN") {
    code = fallbackCode;
  }
  const definition = getErrorDefinition(code);
  return {
    definition,
    code,
    errorId: generateErrorId(),
    message: code === "UNKNOWN" && message ? message : definition.message,
    title: definition.title,
    serverMessage: message,
  };
}
